import gsap from 'gsap'
import './About.css'
import { splitReveal, splitConceal, resplitLines, SplitFx } from '../../utils/text.js'
import { attachHoverUnderline } from '../../utils/underline.js'
import { DURATION, EASE, STAGGER } from '../../../shared/motion.js'

const COPY = {
    text: 'Jiuan dances. A bodily sense of space and rhythm, translated into interfaces — where a web page is not a surface, but space itself. Self-taught, working across design and development, from concept to build. Independent developer.',
    links: [
        { label: 'Instagram', href: 'https://www.instagram.com/jiuanwuu/', external: true },
        { label: 'GitHub', href: 'https://github.com/Wuuups', external: true },
        { label: 'Email', href: 'mailto:jiuanwuu@gmail.com', external: false }
    ],
    city: 'TAIPEI'
}

const TAIPEI_OFFSET_MS = 8 * 60 * 60 * 1000

class AppAbout extends HTMLElement {
    constructor() {
        super()

        // 主文字群組（介紹段落 / 連結列表，各自獨立 split）與動畫快取
        this._textSplit = null
        this._linksSplit = null
        this._contentAnimation = null

        // RWD resize 造成斷行改變時，是否有一次重建 split 結構的請求還沒處理（動畫進行中會延後到播完再補做）
        this._pendingResize = false

        // 右下即時資訊：各自獨立 SplitFx，避免被主文字群組的 revert 波及（見 plan 的 SplitText revert 教訓）
        this.cityFx = null
        this.yearFx = null
        this.timeFx = null

        this._clockId = null
        this._colonBlink = null
    }

    connectedCallback() {
        this._renderDOM()
        this._setupDOMReferences()
        this._setupEvents()
        this._setInteractive(false)
    }

    // ─────────────────────────────────────────────────────────────
    // DOM & Initialization Helpers
    // ─────────────────────────────────────────────────────────────

    _renderDOM() {
        this.innerHTML = /* html */ `
            <p class="about__text">${COPY.text}</p>
            <div class="about_infos">
                <ul class="about__links">
                    ${COPY.links
                    .map((link) => {
                        return `<li class="about__links-item"><button class="about__link" type="button" data-href="${link.href}" data-external="${link.external}"><span class="about__links-label">${link.label}</span></button></li>`
                    })
                    .join('')}
                </ul>
                <div class="about__meta">
                    <span class="about__meta-item about__meta-city"></span>
                    <span class="about__meta-item about__meta-year"></span>
                    <span class="about__meta-item about__meta-time"></span>
                </div>
            </div>
        `
    }

    _setupDOMReferences() {
        this.textEl = this.querySelector('.about__text')
        this.linkEls = [...this.querySelectorAll('.about__link')]
        this.linkLabelEls = [...this.querySelectorAll('.about__links-label')]

        this.cityEl = this.querySelector('.about__meta-city')
        this.yearEl = this.querySelector('.about__meta-year')
        this.timeEl = this.querySelector('.about__meta-time')

        this.cityFx = new SplitFx(this.cityEl)
        this.yearFx = new SplitFx(this.yearEl)
        this.timeFx = new SplitFx(this.timeEl)
    }

    _setupEvents() {
        this.linkEls.forEach((el) => {
            attachHoverUnderline(el)
            el.addEventListener('click', () => {
                const { href, external } = el.dataset
                if (external === 'true') {
                    window.open(href, '_blank', 'noopener')
                } else {
                    window.location.href = href
                }
            })
        })
    }

    // ─────────────────────────────────────────────────────────────
    // Dynamic Updates (Taipei Clock)
    // ─────────────────────────────────────────────────────────────

    /** 換算台北時間 (UTC+8)，不依賴訪客本機時區 */
    _taipeiNow() {
        const d = new Date(Date.now() + TAIPEI_OFFSET_MS)
        const hh = String(d.getUTCHours()).padStart(2, '0')
        const mm = String(d.getUTCMinutes()).padStart(2, '0')
        return {
            year: String(d.getUTCFullYear()),
            time: `${hh}:${mm}(UTC+8)`,
            timeHTML: `${hh}<span class="clock-colon">:</span>${mm} (UTC+8)`
        }
    }

    _startClock() {
        this._stopClock()
        this._clockId = setInterval(() => {
            this._tick()
        }, 1000)
    }

    _stopClock() {
        clearInterval(this._clockId)
        this._clockId = null
    }

    /** 電子鐘式冒號閃爍：0.5s 顯示 / 0.5s 隱藏，硬切無漸層 */
    _startColonBlink() {
        this._colonBlink?.kill()

        const colonEl = this.timeEl?.querySelector('.clock-colon')
        if (!colonEl) {
            this._colonBlink = null
            return
        }

        const tl = gsap.timeline({ repeat: -1 })
        tl.set(colonEl, { opacity: 1 })
        tl.to(colonEl, { opacity: 0, duration: 0 }, 0.5)
        tl.to(colonEl, { opacity: 1, duration: 0 }, 1)
        this._colonBlink = tl
    }

    _stopColonBlink() {
        this._colonBlink?.kill()
        this._colonBlink = null
    }

    /** 每秒跳動：只在值真的變動時才 revert + 換字（比照 Detail.setIndex 的即時更新慣例，不重新 split） */
    _tick() {
        const { year, time, timeHTML } = this._taipeiNow()

        if (year !== this.yearEl.textContent) {
            this.yearFx.revert()
            this.yearEl.textContent = year
        }
        if (time !== this.timeEl.textContent) {
            this.timeFx.revert()
            this.timeEl.innerHTML = timeHTML
            this._startColonBlink()
        }
    }

    // ─────────────────────────────────────────────────────────────
    // State & Interactive Handlers
    // ─────────────────────────────────────────────────────────────

    /** 擋掉非 about 路由下隱形內容仍可被點擊的問題 */
    _setInteractive(active) {
        const value = active ? 'auto' : 'none'
        this.linkEls.forEach((el) => {
            el.style.pointerEvents = value
        })
    }

    // ─────────────────────────────────────────────────────────────
    // Motion Transitions
    // ─────────────────────────────────────────────────────────────

    _cleanupAnimations() {
        this._contentAnimation?.kill()
        this._textSplit?.revert()
        this._linksSplit?.revert()
        this._textSplit = null
        this._linksSplit = null
        this._contentAnimation = null
    }

    /** 進場：面板淡入 + text → links → meta 依序錯開起跑 (STAGGER.loose) */
    splitIn() {
        this._cleanupAnimations()
        this._setInteractive(true)

        const { year, timeHTML } = this._taipeiNow()

        const { split: textSplit, tween: textTween } = splitReveal([this.textEl])
        const { split: linksSplit, tween: linksTween } = splitReveal(this.linkLabelEls)
        this._textSplit = textSplit
        this._linksSplit = linksSplit

        const cityTween = this.cityFx.reveal(COPY.city)
        const yearTween = this.yearFx.reveal(year)
        const timeTween = this.timeFx.reveal(timeHTML, { html: true })

        const tl = gsap.timeline()
        tl.to(this, { autoAlpha: 1, duration: DURATION.spread, ease: EASE.out }, 0)
        if (textTween) {
            tl.add(textTween, 0)
        }
        if (linksTween) {
            tl.add(linksTween, STAGGER.loose)
        }
        if (cityTween) {
            tl.add(cityTween, STAGGER.loose * 2)
        }
        if (yearTween) {
            tl.add(yearTween, STAGGER.loose * 2)
        }
        if (timeTween) {
            tl.add(timeTween, STAGGER.loose * 2)
        }
        this._contentAnimation = tl
        tl.eventCallback('onComplete', () => {
            this._flushPendingResize()
        })

        this._startClock()
        this._startColonBlink()

        return tl
    }

    /** 離場：主文字/連結/右下資訊同時 split 出，播完才輪到面板淡出（讓 split 動作看得見，不被面板淡出蓋掉） */
    splitOut() {
        this._cleanupAnimations()
        this._setInteractive(false)
        this._stopClock()
        this._stopColonBlink()

        const { split: textSplit, tween: textTween } = splitConceal([this.textEl], { stagger: STAGGER.none })
        const { split: linksSplit, tween: linksTween } = splitConceal(this.linkLabelEls, { stagger: STAGGER.none })
        this._textSplit = textSplit
        this._linksSplit = linksSplit

        const cityTween = this.cityFx.conceal()
        const yearTween = this.yearFx.conceal()
        const timeTween = this.timeFx.conceal()

        const tl = gsap.timeline()
        if (textTween) {
            tl.add(textTween, 0)
        }
        if (linksTween) {
            tl.add(linksTween, 0)
        }
        if (cityTween) {
            tl.add(cityTween, 0)
        }
        if (yearTween) {
            tl.add(yearTween, 0)
        }
        if (timeTween) {
            tl.add(timeTween, 0)
        }
        tl.to(this, { autoAlpha: 0, duration: DURATION.spread, ease: EASE.out }, '>')
        this._contentAnimation = tl
        tl.eventCallback('onComplete', () => {
            this._flushPendingResize()
        })

        return tl
    }

    // ─────────────────────────────────────────────────────────────
    // RWD Resize Handling
    // ─────────────────────────────────────────────────────────────

    resize() {
        if (this._contentAnimation?.isActive()) {
            this._pendingResize = true
            return
        }
        this._resplitStatic()
    }

    _flushPendingResize() {
        if (this._pendingResize) {
            this._resplitStatic()
        }
    }

    _resplitStatic() {
        this._pendingResize = false

        if (!this._textSplit && !this._linksSplit) {
            return
        }

        this._textSplit?.revert()
        this._linksSplit?.revert()
        this._textSplit = resplitLines([this.textEl])
        this._linksSplit = resplitLines(this.linkLabelEls)
    }

    disconnectedCallback() {
        this._stopClock()
        this._stopColonBlink()
        this._cleanupAnimations()
        this.cityFx?.revert()
        this.yearFx?.revert()
        this.timeFx?.revert()
    }
}

if (!customElements.get('app-about')) {
    customElements.define('app-about', AppAbout)
}

export const about = document.querySelector('app-about')
